import { useState, useMemo, useCallback, useRef } from 'react';
import { ClipboardList, CheckCircle2 } from 'lucide-react';
import { Card } from '@components/base/layout';
import { actionAnchorId } from '../configs/otherActionsConfig';
import { Text, Icon } from '@components/base/display';
import { Button } from '@components/base/actions';
import { CheckboxGroup } from '@components/base/inputs';
import { EntityForm, useEntityForm } from '@components/integrated/entity-form';
import type {
  EntityFormConfig,
  CheckboxGroupFieldOptions,
  UseEntityFormReturn,
} from '@components/integrated/entity-form';
import {
  applyLayoutOverrides,
  buildPublishedConfigFromEntityForm,
  resolveCardTitle,
  usePublishLayoutConfig,
} from '@components/shared/configurable';
import type { LayoutOverrides } from '@components/shared/configurable';
import { claimflowApiClient } from '../api/claimflowApiClient';
import type { VeosGuaranteeToOpen } from '../api/taskService';
import { WorkflowStepTracker } from './WorkflowStepTracker';
import type { WorkflowStepExecutionSummary } from './WorkflowStepTracker';

interface OpenCoverageCardProps {
  ses: string;
  idSin: string;
  guarantees: VeosGuaranteeToOpen[];
  /**
   * Fired after guarantees are successfully opened, so the parent can refresh
   * dependent data (e.g. EstimationCard). Optional — omitted at baseline.
   */
  onCoverageOpened?: () => void;
  /**
   * Per-tenant field layout overrides injected by the `withComponentRules`
   * HOC. Underscored to match the injected meta-prop convention; `undefined`
   * at baseline ⇒ the form config is returned unchanged (byte-identical
   * render).
   */
  _layoutOverrides?: LayoutOverrides;
}

export function OpenCoverageCard({
  ses,
  idSin,
  guarantees,
  onCoverageOpened,
  _layoutOverrides,
}: Readonly<OpenCoverageCardProps>) {
  // Collapsed on first paint — every card in the claim view's right column
  // opens folded. Driven by state + `onExpandChange` rather than a literal
  // `defaultExpanded={false}`, because `Card` re-applies a literal default on
  // every parent re-render, which would fold the card again mid-edit.
  const [expanded, setExpanded] = useState(false);

  const [submitted, setSubmitted] = useState(false);
  const [openedCodes, setOpenedCodes] = useState<string[]>([]);
  const [failedSteps, setFailedSteps] = useState<WorkflowStepExecutionSummary[]>([]);

  const formRef = useRef<UseEntityFormReturn | null>(null);

  const openedGuaranteeLabels = useMemo(
    () =>
      guarantees
        .filter((g) => openedCodes.includes(g.CD_GAR))
        .map((g) => g.LIB_GAR),
    [guarantees, openedCodes],
  );

  const mutationFn = useCallback(
    async (payload: Record<string, unknown> | FormData) => {
      const codes = payload instanceof FormData
        ? JSON.parse(payload.get('codes_garanties') as string)
        : payload.codes_garanties;
      const garanties = (codes as string[]).map((code) => ({
        code_garantie: code,
      }));
      const response = await claimflowApiClient.post(
        '/claimflow/workflows/claimflow-open-coverage/execute',
        {
          context_data: {
            ses,
            id_sin: idSin,
            garanties,
          },
        },
      );
      return response.data;
    },
    [ses, idSin],
  );

  const baseConfig: EntityFormConfig = useMemo(
    () => ({
      id: 'open-coverage',
      endpoint: {
        erpEndpoint:
          '/api/claimflow/workflows/claimflow-open-coverage/execute',
        method: 'POST',
      },
      fields: {
        codes_garanties: {
          type: 'checkboxGroup',
          label: '',
          validation: {
            required: 'Veuillez sélectionner au moins une garantie',
          },
          options: {
            items: guarantees.map((g) => ({
              value: g.CD_GAR,
              label: g.LIB_GAR,
            })),
            orientation: 'horizontal',
          } as CheckboxGroupFieldOptions,
        },
      },
      actions: {
        submit: { visible: true, label: 'Enregistrer les garanties' },
        cancel: { visible: false },
        reset: { visible: false },
      },
      layout: { columns: 1 },
    }),
    [guarantees],
  );

  usePublishLayoutConfig(
    'claimflow.openCoverageCard',
    buildPublishedConfigFromEntityForm(baseConfig, 'Ouverture de garanties'),
  );

  const formConfig: EntityFormConfig = useMemo(
    () => applyLayoutOverrides(baseConfig, _layoutOverrides),
    [baseConfig, _layoutOverrides],
  );

  const handleSuccess = useCallback(() => {
    const codes =
      (formRef.current?.values.codes_garanties as string[] | undefined) ?? [];
    setOpenedCodes(codes);
    setFailedSteps([]);
    setSubmitted(true);
    onCoverageOpened?.();
  }, [onCoverageOpened]);

  const handleError = useCallback((error: unknown) => {
    const stepExecutions = extractStepExecutions(error);
    setFailedSteps(stepExecutions);
  }, []);

  const form = useEntityForm({
    config: formConfig,
    onSuccess: handleSuccess,
    onError: handleError,
    mutationFn,
  });

  formRef.current = form;

  if (submitted) {
    return (
      <Card key="submitted" id={actionAnchorId('claimflow.openCoverageCard')}>
        <Card.Header collapsible defaultExpanded={false}>
          <Icon icon={CheckCircle2} size="md" color="success" />
          <Text as="span" weight="semibold" size="base">
            {resolveCardTitle('Ouverture de garanties', _layoutOverrides)}
          </Text>
          <Text as="span" size="sm" color="muted" className="ml-2">
            ({openedGuaranteeLabels.join(', ')})
          </Text>
        </Card.Header>
        <Card.Content>
          <CheckboxGroup
            options={guarantees.map((g) => ({
              value: g.CD_GAR,
              label: g.LIB_GAR,
            }))}
            value={openedCodes}
            orientation="horizontal"
            disabled
          />
          <div className="mt-4">
            <Button
              variant="primary"
              leftIcon={<Icon icon={ClipboardList} size="sm" />}
              disabled
              className="w-full"
            >
              Garanties enregistrées
            </Button>
          </div>
        </Card.Content>
      </Card>
    );
  }

  return (
    <Card key="open" id={actionAnchorId('claimflow.openCoverageCard')}>
      <Card.Header
        collapsible
        defaultExpanded={expanded}
        onExpandChange={setExpanded}
      >
        <Icon icon={ClipboardList} size="md" color="primary" />
        <Text as="span" weight="semibold" size="base">
          {resolveCardTitle('Ouverture de garanties', _layoutOverrides)}
        </Text>
      </Card.Header>
      <Card.Content>
        <EntityForm config={formConfig} form={form}>
          <EntityForm.Alert />
          {failedSteps.length > 0 && (
            <div className="mb-4">
              <Text size="sm" weight="semibold" className="mb-2">
                Détail de l&apos;exécution :
              </Text>
              <WorkflowStepTracker steps={failedSteps} />
            </div>
          )}
          <EntityForm.Fields />
          {formConfig.actions?.submit?.visible !== false && (
            <div className="pt-4">
              <Button
                type="submit"
                variant="primary"
                leftIcon={<Icon icon={ClipboardList} size="sm" />}
                loading={form.isSubmitting}
                disabled={form.isSubmitting}
                className="w-full"
              >
                {formConfig.actions?.submit?.label ??
                  'Enregistrer les garanties'}
              </Button>
            </div>
          )}
        </EntityForm>
      </Card.Content>
    </Card>
  );
}

function extractStepExecutions(error: unknown): WorkflowStepExecutionSummary[] {
  const data = getErrorData(error);
  if (!data) return [];

  const executions = (data as Record<string, unknown>).step_executions;
  if (!Array.isArray(executions)) return [];

  return executions.map((exec: Record<string, unknown>) => ({
    name: (exec.step as Record<string, unknown>)?.name as string ?? `Étape ${String(exec.workflow_step_id).slice(0, 8)}`,
    status: exec.status as WorkflowStepExecutionSummary['status'],
    errorMessage: exec.error_message as string | null ?? null,
  }));
}

function getErrorData(error: unknown): unknown {
  if (error === null || typeof error !== 'object') return null;
  const err = error as Record<string, unknown>;

  const response = err.response;
  if (response !== null && typeof response === 'object') {
    const respObj = response as Record<string, unknown>;
    const respData = respObj.data;
    if (respData !== null && typeof respData === 'object') {
      const nested = (respData as Record<string, unknown>).data;
      if (nested !== null && typeof nested === 'object') return nested;
      return respData;
    }
  }

  const data = err.data;
  if (data !== null && typeof data === 'object') return data;

  return null;
}
